import React from "react";
import { Link } from "react-router-dom";
import stats from "../../data/atlasStats.json";
import "../../styles/Understand.css";

// The positioning section: what a reader actually gets to understand, and the
// fact that the answer is specific to them rather than to an average person.
// It replaced the standalone pregnancy/breastfeeding section, which was strong
// but narrow — it spoke to a small slice of visitors and is not the strongest
// claim the site can make.
//
// Two claims are deliberately NOT made here, because the data does not support
// them and a checkable overstatement would cost more than it buys:
//
//   * "at a cellular level" — there is no constituent/mechanism layer.
//     kb_constituent_bridge exists but holds 0 rows, so the honest ceiling is
//     the pharmacological ACTION (antiinflammatory, antispasmodic, sedative),
//     of which there are 1,136 therapeutic ones. If that table is ever
//     populated, this is the section to revisit.
//
//   * "all backed by science and research" — 40,890 of the 64,061
//     relationships are traditional use with no science reviewed. The claim we
//     can make, and the one that survives being checked, is that every claim is
//     GRADED against the research, absence included.
const CARDS = [
  {
    key: "actions",
    stat: (s) => "1,136",
    statLabel: "documented actions",
    title: "What a plant does in your body",
    body: "Not “good for sleep”. The actual documented actions — sedative, antispasmodic, anti-inflammatory — and the conditions each one bears on. This is the difference between a remedy list and knowing why something is being suggested to you.",
  },
  {
    key: "you",
    stat: () => "yours",
    statLabel: "not the average body",
    title: "Which ones fit your biology",
    body: "Your constitution, your medications, your history. The same complaint in two people rarely points at the same plant, and anything that ignores that is guessing. Tell us how your body works and the map narrows to what applies to you.",
  },
  {
    key: "together",
    stat: (s) => (s.herbs ? s.herbs.toLocaleString() : "1,056"),
    statLabel: "plants, cross-referenced",
    title: "How they behave together",
    body: "Plants are rarely taken alone, and combinations are where most advice goes quiet. We build combinations that cover more of what your body is asking for, and surface the interactions instead of leaving you to find them.",
  },
  {
    key: "evidence",
    stat: () => "23,171",
    statLabel: "claims with science behind them",
    title: "What the research actually says",
    body: "Every relationship carries its grade — human trial, laboratory work, or traditional use with no science reviewed — and the references behind it. You are never asked to take our word for it, which is the whole point.",
  },
];

const UnderstandSection = () => (
  <section className="sv-dark sv-section understand">
    <div className="sv-wrap">
      <p className="sv-eyebrow understand__eyebrow">What you'll understand</p>
      <h2 className="sv-h2 sv-center understand__title">
        Stop guessing which plant, and why.
      </h2>
      <p className="sv-body sv-center understand__intro">
        Most herbal advice hands you a list and hopes. This is the other thing —
        understanding what these plants do, which of them suit the body you
        actually have, and what the research does and doesn't support.
      </p>

      <ul className="understand__grid">
        {CARDS.map((c) => (
          <li key={c.key} className={`sv-card understand__card understand__card--${c.key}`}>
            <span className="understand__stat">{c.stat(stats)}</span>
            <span className="understand__stat-label">{c.statLabel}</span>
            <h3 className="sv-h4 understand__card-title">{c.title}</h3>
            <p className="understand__card-body">{c.body}</p>
          </li>
        ))}
      </ul>

      <p className="sv-center understand__cta">
        <Link className="understand__link" to="/atlas">
          See it for yourself in the Atlas →
        </Link>
      </p>
    </div>
  </section>
);

export default UnderstandSection;
